import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Separator, Text, Button, FlexView, Col } from '@cuemath/comps';
import COLORS from 'constants/colors';
import { Head } from 'modules/form';
import { LoaderAlt } from 'styled-icons/boxicons-regular/LoaderAlt';
import { CheckCircle } from 'styled-icons/boxicons-regular/CheckCircle';
import crossCircle from 'img/white-cross-icon.svg';
import { TextInput } from '@cuemath/web-comps';
import { useEmailPost, useEmailPatch } from '../actions/email-update';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { closeModal } from 'actions/modal';
import { Table, TableCell } from 'comps';
import { TableRow } from 'semantic-ui-react';

const InputWrapper = styled(FlexView)`
  width: 260px;
  height: 44px;
  margin: 0 auto;
`;

const CrossIcon = styled.div`
  position: absolute;
  cursor: pointer;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  right: -44px;
  top: -35px;
`;

const Wrapper = styled.div`
  padding-right: 10px;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`;

const StyledButton = styled(Button)`
  margin: 20px auto;
  width: 230px;
`;

const StyledText = styled(Text)`
  margin-left: 50px;
`;

const LoaderIcon = styled(LoaderAlt)`
  margin: 40px auto;
  height: 140px;
  animation: ${spin} 1s linear infinite;
`;

const CheckCircleIcon = styled(CheckCircle)`
  margin: 40px auto;
  height: 140px;
  animation: ${fadeIn} 0.5s linear;
`;

const EditStudentEmailContainer = ({ student }) => {
  const dispatch = useDispatch();
  const [showLoader, setShowLoader] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [errorText, setErrorText] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [showUpdateButton, setShowUpdateButton] = React.useState(false);
  const [emailAvailabilityData, setEmailAvailabilityData] = React.useState([]);
  const { parent_data: parentData } = student || {};
  const { primary_user_id: parentId } = parentData || {};

  const closeEmailModal = () => {
    dispatch(closeModal());
  };

  const onEmailPatchComplete = (errorMsg, data) => {
    const { data: userData } = data ?? {};
    const { success } = userData ?? {};
    setShowLoader(false);
    if (success) {
      setShowSuccess(true);
    } else {
      setErrorText(errorMsg || 'something went wrong');
    }
  };
  const onEmailVerifyComplete = (errorMsg, data) => {
    const { data: userData } = data || {};
    const { can_update: canUpdate, existing_users: existingUsers } = userData ?? {};
    if (canUpdate) {
      setEmailAvailabilityData(existingUsers);
      setShowUpdateButton(true);
    } else {
      setEmailAvailabilityData(existingUsers);
    }
  };

  const { post: verifyEmailPost, isProcessing } = useEmailPost({
    onComplete: onEmailVerifyComplete,
  });
  const { patch: updateEmailPatch } = useEmailPatch({
    onComplete: onEmailPatchComplete,
  });

  const handleChange = value => {
    setEmail(value);
  };

  const handlePatchAction = () => {
    setShowLoader(true);
    setErrorText('');
    updateEmailPatch(parentId, { email });
  };

  const handleCheckEmail = () => {
    verifyEmailPost({ email }, parentId);
  };

  useEffect(() => {
    if (isProcessing) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
  }, [isProcessing]);

  return (
    <Wrapper>
      {!showLoader && (
        <CrossIcon onClick={closeEmailModal}>
          <img src={crossCircle} width="32" height="32" alt="closed-icon" />
        </CrossIcon>
      )}
      <Head>
        <Text size="xlarge" align="center">
          Edit Email
        </Text>
      </Head>
      <Separator transparent height="20" />
      <Separator transparent height="25" />

      {showLoader && (
        <>
          <LoaderIcon color={COLORS.BOSTON_BLUE} />
        </>
      )}
      {showSuccess && <CheckCircleIcon color={COLORS.DARK_GREEN} />}
      {!showLoader && !showSuccess && (
        <>
          {errorText && (
            <>
              <StyledText color={COLORS.RED} align="center">
                {errorText}
              </StyledText>
              <Separator transparent height="25" />
            </>
          )}
          <InputWrapper>
            <TextInput
              type="email"
              label="Email"
              value={email}
              onChange={value => handleChange(value)}
              disable={showUpdateButton}
            />
          </InputWrapper>

          {showUpdateButton ? (
            <StyledButton onClick={handlePatchAction} label="Yes, Update email" />
          ) : (
            <StyledButton onClick={handleCheckEmail} label="Check" />
          )}
          {!!emailAvailabilityData?.length && (
            <Col>
              <Table
                scrollBodyHeight="170px"
                headerCell={[
                  { title: 'Phone no.' },
                  { title: 'Parent Id' },
                  { title: 'Verified' },
                ]}
                showLoader={isProcessing}
                dataLength={emailAvailabilityData?.length}
                showInfo={false}
              >
                {emailAvailabilityData?.map(el => {
                  const { user_id, phone, verified_email } = el || {};
                  return (
                    <TableRow key={user_id}>
                      <TableCell width={3}>{phone || '-'}</TableCell>
                      <TableCell width={3}>{user_id || '-'}</TableCell>
                      <TableCell width={3}>{verified_email ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  );
                })}
              </Table>
            </Col>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default EditStudentEmailContainer;
